/**
 * QuoteForm Component - Form for creating quotes
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  CloudDone,
  CloudQueue,
  Refresh,
} from "@mui/icons-material";
import { createQuote } from "../services/quoteService.js";
import { resolveQuoteDisplayTotal } from "../services/automatedPreparationReviewService.js";
import EstimatePricingFab from "./EstimatePricingFab.jsx";
import CustomerSearchAutocomplete from "../../../shared/components/CustomerSearchAutocomplete.jsx";
import LeadSearchAutocomplete from "../../../shared/components/LeadSearchAutocomplete.jsx";
import PriceCardLineItemEditor from "../../priceCard/components/PriceCardLineItemEditor.jsx";
import { clearPriceCardSnapshot } from "../../priceCard/utils/lineItemPricing.js";
import CreateProductModal from "../../products/components/CreateProductModal.jsx";
import SupplierSelect from "../../../shared/components/SupplierSelect.jsx";
import { GoogleAddressAutocomplete } from "../../../components/AddressInput/GoogleAddressAutocomplete.jsx";
import { loadGoogleMapsScript } from "../../../shared/utils/loadGoogleMaps.js";
import api from "../../../shared/api.jsx";
import { updateLead } from "../../leads/repositories/leadRepository.js";
import { useFormAutoSave } from "../../../hooks/useFormAutoSave.js";
import { formatCurrency } from "../../../shared/utils/formatters.js";
import { logWarn, logError } from "../../../shared/utils/logger.js";
import { formatLeadItemsList } from "../../../shared/utils/leadItems.js";
import { formatDisplayError } from "../../../shared/utils/displayError.js";
import { Add, Delete } from "@mui/icons-material";

/**
 * QuoteForm Component
 */
const QuoteForm = ({ onMissingTariff }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("leadId");
  // Check if this is a public customer quote request (accessed from /quote-request only)
  const isPublicRequest = location.pathname === "/quote-request";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createProductModal, setCreateProductModal] = useState({
    open: false,
    index: null,
    name: "",
  });
  const [success, setSuccess] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const [formData, setFormData] = useState({
    customer: null,
    address: "",
    from: "",
    to: "",
    pickupAddressData: null, // Structured pickup address data
    deliveryAddressData: null, // Structured delivery address data
    date: "", // Deprecated: kept for backward compatibility
    dates: [""], // Array of dates
    category: "moving",
    items: [], // Start with empty array - form hidden by default
    itemsList: "", // Separate field for listing items to be moved (for moving quotes)
    notes: "",
    estimatedCost: "",
    status: "pending",
    // SOP #1: Truck size
    truckSize: "",
    // SOP #2: Load type (required when leadId is present)
    loadType: "",
    serviceType: "",
    // Note: addOns and addOnsCost removed - now handled as products in items array
  });

  // Auto-save hook
  const { hasDraft, restoreDraft, clearDraft, isSaving } = useFormAutoSave(
    formData,
    "quote-form-draft",
    {
      debounceMs: 1500, // Save 1.5 seconds after user stops typing
      shouldSave: (data) => {
        // Only save if there's meaningful data
        return !!(
          data.customer ||
          data.from ||
          data.to ||
          data.items ||
          data.estimatedCost
        );
      },
    }
  );

  // Helper function to calculate total from line items
  const calculateItemsTotal = (items) => {
    return items.reduce((sum, item) => {
      const quantity = Number(item.quantity ?? 1);
      const unitPrice = item.unitPrice === "" || item.unitPrice === null || item.unitPrice === undefined
        ? 0
        : Number(item.unitPrice ?? 0);
      const itemTotal = item.total ?? quantity * unitPrice;
      return sum + itemTotal;
    }, 0);
  };

  // Add new line item
  const addLineItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          product: null,
          productId: null,
          description: "",
          quantity: 1,
          unitPrice: "", // Start blank, not 0 - prevents accidental free items
          total: 0,
          supplier: "bubakii",
          isCustom: true,
          from: "",
          to: "",
        },
      ],
    });
  };

  // Remove line item
  const removeLineItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        items: newItems,
      });
    }
  };

  // Handle line item change
  const handleLineItemChange = (index, field) => (e) => {
    const newItems = [...formData.items];
    const value = e.target ? e.target.value : e; // Support both event and direct value

    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    // Recalculate total if unitPrice or quantity changed - always compute, even if unitPrice is 0 (free item)
    if (field === "unitPrice" || field === "quantity") {
      const quantity = Number(newItems[index].quantity ?? 1);
      const unitPrice = newItems[index].unitPrice === "" || newItems[index].unitPrice === null || newItems[index].unitPrice === undefined
        ? 0
        : Number(newItems[index].unitPrice ?? 0);
      newItems[index].total = Math.round(quantity * unitPrice * 100) / 100;
    }

    setFormData({
      ...formData,
      items: newItems,
    });
  };

  const replaceLineItem = (index) => (lineItem) => {
    const newItems = [...formData.items];
    newItems[index] = lineItem;
    setFormData({ ...formData, items: newItems });
  };

  // Handle product selection
  const handleProductSelect = (index) => (product) => {
    const newItems = [...formData.items];
    const currentItem = newItems[index];
    
    // Use nullish coalescing to handle 0 correctly (0 is valid, null/undefined should use product price)
    const effectiveUnitPrice = product?.unitPrice ?? currentItem.unitPrice ?? "";
    
    newItems[index] = {
      ...clearPriceCardSnapshot(currentItem),
      product: product,
      productId: product?.id || null,
      description: product?.name || currentItem.description,
      unitPrice: effectiveUnitPrice,
      isCustom: !product,
      // Auto-populate supplier from product defaultSupplier
      supplier:
        product?.defaultSupplier ?? currentItem.supplier ?? "bubakii",
    };

    // Recalculate total - use effective price (product price if blank)
    const quantity = Number(currentItem.quantity ?? 1);
    const unitPrice = effectiveUnitPrice === "" ? 0 : Number(effectiveUnitPrice ?? 0);
    newItems[index].total = Math.round(quantity * unitPrice * 100) / 100;

    setFormData({
      ...formData,
      items: newItems,
    });
  };

  // Handle custom item entry
  const handleCustomItem = (index) => (description) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...clearPriceCardSnapshot(newItems[index]),
      product: null,
      productId: null,
      description: description,
      isCustom: true,
      persistProduct: true,
    };

    setFormData({
      ...formData,
      items: newItems,
    });
  };

  const openCreateProductModal = (index, name) => {
    setCreateProductModal({ open: true, index, name });
  };

  const closeCreateProductModal = () => {
    setCreateProductModal({ open: false, index: null, name: "" });
  };

  // Restore draft on mount (but only if no leadId and no selectedLead)
  useEffect(() => {
    if (!leadId && !selectedLead) {
      const draft = restoreDraft();
      if (draft) {
        // Normalize draft data to ensure dates is always an array
        // and items is always an array
        let normalizedItems = [];

        if (draft.items) {
          if (Array.isArray(draft.items) && draft.items.length > 0) {
            // Already in new format - ensure from/to fields exist
            normalizedItems = draft.items.map((item) => ({
              ...item,
              from: item.from || "",
              to: item.to || "",
            }));
          } else if (typeof draft.items === "string") {
            // Old string format - convert to array
            normalizedItems = [
              {
                product: null,
                productId: null,
                description: draft.items,
                quantity: 1,
                unitPrice: 0,
                total: 0,
                supplier: "bubakii",
                isCustom: true,
                from: "",
                to: "",
              },
            ];
          }
        }

        const normalizedDraft = {
          ...draft,
          dates:
            Array.isArray(draft.dates) && draft.dates.length > 0
              ? draft.dates
              : draft.date
                ? [draft.date]
                : [""],
          items: normalizedItems,
          itemsList: draft.itemsList || "", // Preserve itemsList if it exists
        };
        setFormData(normalizedDraft);
        setDraftRestored(true);
        // Auto-hide the alert after 5 seconds
        setTimeout(() => {
          setDraftRestored(false);
        }, 5000);
      }
    }
  }, [restoreDraft, leadId, selectedLead]);

  // Load lead data and pricing suggestion if leadId is provided
  useEffect(() => {
    const loadLeadData = async () => {
      if (!leadId) return;

      try {
        // Load lead data
        const leadResponse = await api.get(`/leads/${leadId}`);
        const lead =
          leadResponse.data?.data || leadResponse.data || leadResponse;

        // Set selected lead for UI consistency
        setSelectedLead(lead);

        // Pre-populate customer from lead (customerId preferred; phone/name still prefill)
        if (lead.customerId || lead.phone || lead.name || lead.email) {
          const leadCustomer = {
            customerId: lead.customerId || null,
            _id: lead.customerId || null,
            name: lead.name || lead.contactName || "",
            phone: lead.phone || "",
            email: lead.contactInfo?.email || lead.email || null,
            address: lead.address || lead.contactInfo?.address || null,
          };
          setFormData((prev) => ({
            ...prev,
            customer: lead.customerId
              ? leadCustomer
              : {
                  ...prev.customer,
                  ...leadCustomer,
                  customerId: prev.customer?.customerId || leadCustomer.customerId,
                  _id: prev.customer?._id || leadCustomer._id,
                },
            address: lead.address || lead.contactInfo?.address || prev.address,
          }));
        }

        // Pre-fill form with lead data
        // Support both legacy move_date and new move_dates array
        const leadDates =
          lead.move_dates && lead.move_dates.length > 0
            ? lead.move_dates
            : lead.move_date
              ? [lead.move_date]
              : [""];

        // Pre-populate structured address data if available
        const pickupAddressData = lead.pickupAddress || null;
        const deliveryAddressData = lead.deliveryAddress || null;

        // For moving quotes: itemsList contains the items to be moved
        // Line items represent move routes (from/to)
        let itemsList = "";
        let itemsArray = [];

        if (lead.category === "moving") {
          // For moving quotes: convert lead.items to itemsList string
          itemsList = formatLeadItemsList(lead.items);

          // Initialize line items as empty array - form hidden by default
          // User must click "Add Line Item" button to add line items
          itemsArray = [];
        }

        // Prefer automatedPricing truck (not legacy pricing-suggestion).
        const adrTruck =
          lead.automatedPricing?.selectedTruckSize ||
          lead.automatedPricing?.recommendedTruckSize ||
          "";

        setFormData((prev) => ({
          ...prev,
          from:
            lead.moving_from ||
            lead.pickupAddress?.address ||
            "",
          to:
            lead.moving_to ||
            lead.deliveryAddress?.address ||
            "",
          pickupAddressData: pickupAddressData,
          deliveryAddressData: deliveryAddressData,
          date: lead.move_date || "", // Keep for backward compatibility
          dates: leadDates, // Load all dates from lead
          items: itemsArray,
          itemsList: itemsList, // Items to be moved (for moving quotes)
          notes: lead.notes || "",
          category: lead.category || prev.category,
          serviceType: lead.qualification?.serviceType || prev.serviceType,
          ...(adrTruck ? { truckSize: adrTruck } : {}),
        }));
      } catch (err) {
        logError("QuoteForm: error loading lead data", err);
      }
    };

    loadLeadData();
  }, [leadId]);

  // Load Google Maps script on mount
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (apiKey) {
      loadGoogleMapsScript(apiKey).catch((err) => {
        logError("QuoteForm: Failed to load Google Maps script", err);
      });
    }
  }, []);

  // GoogleAddressAutocomplete passes the string value, not a DOM event (see ManualLeadForm pattern).
  const handleChange = (field) => (e) => {
    const value =
      typeof e === "string" || typeof e === "number"
        ? e
        : e?.target?.type === "checkbox"
          ? e.target.checked
          : e?.target?.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
  };

  const handlePlaceSelect = (type) => (addressData) => {
    setError(null);
    setFormData((prev) => ({
      ...prev,
      [type === "pickup" ? "from" : "to"]: addressData.address,
      [`${type === "pickup" ? "pickup" : "delivery"}AddressData`]: addressData,
    }));
  };

  // Note: handleAddOnChange and handleAddOnQuantityChange removed
  // Add-ons are now handled as products in the line items array

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate required fields
    if (!formData.customer) {
      setError("Customer is required");
      setLoading(false);
      return;
    }

    // Enforce name + email for PWA/public requests
    if (isPublicRequest) {
      const customerName = formData.customer.name;
      const customerEmail = formData.customer.email;
      if (!customerName || !customerName.trim()) {
        setError("Name is required");
        setLoading(false);
        return;
      }
      if (!customerEmail || !customerEmail.trim()) {
        setError("Email is required");
        setLoading(false);
        return;
      }
    }

    // Validate at least one date is provided
    const datesArray = Array.isArray(formData.dates)
      ? formData.dates
      : formData.date
        ? [formData.date]
        : [""];
    const validDates = datesArray.filter((d) => d && d.trim() !== "");
    if (validDates.length === 0) {
      setError("At least one move date is required");
      setLoading(false);
      return;
    }

    const routeFrom =
      (formData.from && String(formData.from).trim()) ||
      (formData.pickupAddressData?.address &&
        String(formData.pickupAddressData.address).trim()) ||
      "";
    const routeTo =
      (formData.to && String(formData.to).trim()) ||
      (formData.deliveryAddressData?.address &&
        String(formData.deliveryAddressData.address).trim()) ||
      "";
    if (!routeFrom || !routeTo) {
      setError("Moving from and moving to addresses are required");
      setLoading(false);
      return;
    }

    // Validate based on category and prepare items array
    let itemsArray = [];

    // For all quote types: validate line items with description, unit price, and supplier
    if (formData.category === "moving") {
      // For moving quotes: also validate itemsList
      if (!formData.itemsList || formData.itemsList.trim().length === 0) {
        setError("Items to be moved are required for moving quotes");
        setLoading(false);
        return;
      }
    }

    // Validate line items if provided (line items are optional)
    const validItems = formData.items.filter(
      (item) =>
        item.description &&
        item.description.trim().length > 0 &&
        item.unitPrice !== undefined &&
        item.unitPrice !== null &&
        item.unitPrice >= 0 &&
        item.supplier &&
        item.supplier.trim().length > 0
    );
    if (
      validItems.some(
        (item) =>
          item.pricingOverride?.applied === true &&
          !item.pricingOverride?.reason?.trim(),
      )
    ) {
      setError("A reason is required for every price-card override");
      setLoading(false);
      return;
    }

    // If items are provided, validate they have required fields
    if (formData.items && formData.items.length > 0) {
      // Validate all items have supplier
      const itemsWithoutSupplier = validItems.filter((item) => !item.supplier);
      if (itemsWithoutSupplier.length > 0) {
        setError("All line items must have a supplier selected");
        setLoading(false);
        return;
      }
    }

    // Convert line items to quote items format (same for all quote types)
    // Only include valid items if any are provided (line items are optional)
    itemsArray =
      validItems.length > 0
        ? validItems.map((item) => ({
            type: "text",
            description: item.description.trim(),
            quantity: Number(item.quantity) || 1,
            productId: item.productId || null,
            isCustom: item.isCustom !== false, // Default to true if not set
            supplier: item.supplier || "bubakii", // Required field
            unitPrice:
              item.unitPrice === "" || item.unitPrice == null
                ? undefined
                : Number(item.unitPrice),
            total:
              item.total === "" || item.total == null
                ? undefined
                : Number(item.total),
            persistProduct: item.persistProduct || false, // Include persistProduct flag
            priceCardElementId: item.priceCardElementId || null,
            priceCardId: item.priceCardId || null,
            priceCardVersion: item.priceCardVersion ?? null,
            unit: item.unit || null,
            pricingOverride: item.pricingOverride || undefined,
          }))
        : [];

    // SOP #2: Validate loadType when leadId or selectedLead is present
    const currentLeadId = selectedLead?.id || selectedLead?._id || leadId;
    if (currentLeadId && !formData.loadType) {
      setError("Load type is required when creating quote from lead");
      setLoading(false);
      return;
    }

    // Note: Add-ons removed - cost is now calculated from line items

    // Calculate final estimated cost: items total + manual override (if provided)
    const itemsTotal = calculateItemsTotal(validItems);
    const finalEstimatedCost = resolveQuoteDisplayTotal(
      itemsTotal,
      formData.estimatedCost
    );

    // Parse basic items (itemsList) into array format for backend
    // Only include if quote has itemSetId (linked to Lead) or will be auto-created
    let listItemsArray = undefined;
    const currentItemSetId = selectedLead?.itemSetId || null;

    if (formData.itemsList && formData.itemsList.trim().length > 0) {
      const parseItemString = (itemStr) => {
        const trimmed = itemStr.trim();
        if (!trimmed) return null;

        // Match pattern like "2 queen size beds" or "1 stove"
        const match = trimmed.match(/^(\d+)\s+(.+)$/);
        if (match) {
          return {
            type: "text",
            content: match[2].trim(),
            quantity: parseInt(match[1], 10),
          };
        }
        // If no quantity prefix, just use the content
        return {
          type: "text",
          content: trimmed,
        };
      };

      listItemsArray = formData.itemsList
        .split(",")
        .map((item) => parseItemString(item))
        .filter(
          (item) => item && item.content && item.content.trim().length > 0
        );
    }

    // Prepare quote data
    const quoteData = {
      customerId: formData.customer.customerId || formData.customer._id || null,
      customerName: formData.customer.name,
      customerPhone: formData.customer.phone || null,
      customerEmail: formData.customer.email || null,
      address: formData.address || formData.customer.address || null,
      from: routeFrom,
      to: routeTo,
      pickupAddress: formData.pickupAddressData || null, // Structured pickup address
      deliveryAddress: formData.deliveryAddressData || null, // Structured delivery address
      date:
        formData.date ||
        (Array.isArray(formData.dates) && formData.dates.length > 0
          ? formData.dates[0]
          : "Not specified"), // Keep for backward compatibility
      dates: (Array.isArray(formData.dates) ? formData.dates : []).filter(
        (d) => d && d.trim() !== ""
      ), // Filter out empty dates
      category: formData.category || "moving",
      items: itemsArray, // LINE ITEMS with pricing only - stays on quote
      listItems: listItemsArray, // BASIC ITEMS - updates lead's items when itemSetId exists
      notes: formData.notes || "",
      estimatedCost: finalEstimatedCost,
      status: formData.status || "pending",
      // SOP #1: Truck size and add-ons
      truckSize: formData.truckSize || null,
      // SOP #2: Load type (required when leadId is present)
      loadType: formData.loadType || null,
      serviceType: formData.serviceType || null,
      // Note: addOns and addOnsCost deprecated - products are now in items array
      addOns: null,
      addOnsCost: 0,
      leadId: currentLeadId || null,
      itemSetId: currentItemSetId, // Pass itemSetId if available
    };

    try {
      if (currentLeadId && formData.serviceType) {
        try {
          await updateLead(currentLeadId, {
            qualification: { serviceType: formData.serviceType },
          });
        } catch (persistErr) {
          logWarn("QuoteForm: failed to persist service type on lead", persistErr);
          setError(
            formatDisplayError(
              persistErr,
              "Could not save service type on the lead. Quote was not created."
            )
          );
          return;
        }
      }

      // Use public endpoint if accessed from /quote-request route
      const response = await createQuote(quoteData, {
        skipAuth: isPublicRequest,
      });

      if (response.success) {
        // Clear draft on successful submission
        clearDraft();
        setSuccess(true);
        // Clear form after successful save
        handleReset();
        const createdQuote = response.data?.data || response.data;
        const newQuoteId = response.quoteId || createdQuote?.id;
        setTimeout(() => {
          // For public requests, redirect to customer quotes page
          // For admin requests, redirect to quote details page
          if (isPublicRequest) {
            const phone = formData.customerPhone || quoteData.customerPhone;
            if (phone) {
              const normalizedPhone = phone.startsWith("+")
                ? phone.replace(/\s/g, "")
                : `+27${phone.replace(/\s/g, "")}`;
              navigate(
                `/my-quotes?phone=${encodeURIComponent(normalizedPhone)}`
              );
            } else {
              navigate("/my-quotes");
            }
          } else {
            navigate(`/quotes/${newQuoteId}`, {
              state: { quote: createdQuote },
            });
          }
        }, 1000);
      } else {
        // Show detailed error message if available
        setError(formatDisplayError(response, "Failed to create quote"));
      }
    } catch (err) {
      setError(formatDisplayError(err, "Failed to create quote"));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    // Reset form to initial state
    setFormData({
      customer: null,
      address: "",
      from: "",
      to: "",
      pickupAddressData: null,
      deliveryAddressData: null,
      date: "",
      dates: [""],
      category: "moving",
      items: [], // Start with empty array - form hidden by default
      itemsList: "",
      notes: "",
      estimatedCost: "",
      status: "pending",
      truckSize: "",
      loadType: "",
      serviceType: "",
    });
    setSelectedLead(null);
    setError(null);
    setSuccess(false);
    clearDraft();
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        {!isPublicRequest && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/quotes")}
            variant="outlined"
          >
            Back to Quotes
          </Button>
        )}
        <Typography variant="h4">
          {isPublicRequest ? "Bubakii Move Request" : "Create New Quote"}
        </Typography>
        {/* Draft indicator */}
        {hasDraft && !isSaving && (
          <Chip
            icon={<CloudQueue />}
            label="Draft saved"
            color="info"
            size="small"
            sx={{ ml: "auto" }}
          />
        )}
        {isSaving && (
          <Chip
            icon={<CloudDone />}
            label="Saving..."
            color="default"
            size="small"
            sx={{ ml: "auto" }}
          />
        )}
      </Box>

      <Card>
        <CardContent>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {formatDisplayError(error, "Failed to create quote")}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Quote created successfully! Redirecting...
            </Alert>
          )}

          {/* Show draft restored notification */}
          {draftRestored && !error && !success && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              onClose={() => setDraftRestored(false)}
            >
              Your previous draft has been restored. Your work is automatically
              saved.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Lead Search Section - Hidden for public requests */}
              {!isPublicRequest && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Link to Existing Lead (Optional)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Search for an existing lead to pre-populate the form. You
                    can search by customer name, phone number, or email.
                  </Typography>
                  <LeadSearchAutocomplete
                    value={selectedLead}
                    showQuotedStatus={true}
                    onChange={(lead) => {
                      setSelectedLead(lead);
                      if (lead) {
                        const leadItemsList = formatLeadItemsList(lead.items);
                        // Pre-populate form with lead data
                        setFormData((prev) => ({
                          ...prev,
                          // Pre-populate customer if lead has customer info
                          customer: lead.customerId
                            ? {
                                customerId: lead.customerId,
                                _id: lead.customerId,
                                name: lead.name || "",
                                phone: lead.phone || "",
                                email: lead.contactInfo?.email || null,
                                address:
                                  lead.address ||
                                  lead.contactInfo?.address ||
                                  null,
                              }
                            : prev.customer,
                          address:
                            lead.address ||
                            lead.contactInfo?.address ||
                            prev.address,
                          from:
                            lead.moving_from ||
                            lead.pickupAddress?.address ||
                            prev.from,
                          to:
                            lead.moving_to ||
                            lead.deliveryAddress?.address ||
                            prev.to,
                          pickupAddressData: lead.pickupAddress || prev.pickupAddressData,
                          deliveryAddressData: lead.deliveryAddress || prev.deliveryAddressData,
                          date: lead.move_date || prev.date,
                          items: [],
                          itemsList: leadItemsList || prev.itemsList,
                          notes: lead.notes || prev.notes,
                          category: lead.category || prev.category,
                          serviceType:
                            lead.qualification?.serviceType || prev.serviceType,
                          // automatedPricing only (no legacy pricing-suggestion).
                          truckSize:
                            lead.automatedPricing?.selectedTruckSize ||
                            lead.automatedPricing?.recommendedTruckSize ||
                            prev.truckSize,
                          // Note: addOns removed - products should be added as line items
                        }));
                      }
                    }}
                  />
                  {selectedLead && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Form pre-populated from lead. You can modify any fields
                      before creating the quote.
                    </Alert>
                  )}
                </Grid>
              )}

              {/* Customer Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <CustomerSearchAutocomplete
                  value={formData.customer}
                  onChange={(customer) => {
                    setFormData({
                      ...formData,
                      customer: customer,
                      // Pre-fill address if customer has one
                      address: customer?.address || formData.address,
                    });
                    setError(null);
                  }}
                  label="Customer *"
                  required
                  error={
                    error && !formData.customer ? "Customer is required" : null
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={handleChange("address")}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Lead Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Lead Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <GoogleAddressAutocomplete
                  label="Moving From"
                  value={formData.from}
                  onChange={handleChange("from")}
                  onPlaceSelect={handlePlaceSelect("pickup")}
                  placeholder="Start typing your pickup address..."
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <GoogleAddressAutocomplete
                  label="Moving To"
                  value={formData.to}
                  onChange={handleChange("to")}
                  onPlaceSelect={handlePlaceSelect("delivery")}
                  placeholder="Start typing your destination address..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Move Dates *
                </Typography>
                {(Array.isArray(formData.dates) ? formData.dates : [""]).map(
                  (date, index) => (
                    <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                      <Grid item xs={11}>
                        <TextField
                          fullWidth
                          label={`Date ${index + 1}`}
                          type="date"
                          value={date}
                          onChange={(e) => {
                            const currentDates = Array.isArray(formData.dates)
                              ? formData.dates
                              : [""];
                            const newDates = [...currentDates];
                            newDates[index] = e.target.value;
                            setFormData({ ...formData, dates: newDates });
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        {(Array.isArray(formData.dates) ? formData.dates : [""])
                          .length > 1 && (
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              const currentDates = Array.isArray(formData.dates)
                                ? formData.dates
                                : [""];
                              const newDates = currentDates.filter(
                                (_, i) => i !== index
                              );
                              setFormData({ ...formData, dates: newDates });
                            }}
                            sx={{ mt: 1, minWidth: "auto" }}
                          >
                            ×
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  )
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    const currentDates = Array.isArray(formData.dates)
                      ? formData.dates
                      : [""];
                    setFormData({
                      ...formData,
                      dates: [...currentDates, ""],
                    });
                  }}
                  sx={{ mt: 1 }}
                >
                  + Add Another Date
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={formData.category}
                  onChange={handleChange("category")}
                >
                  <MenuItem value="moving">Moving</MenuItem>
                  <MenuItem value="delivery">Delivery</MenuItem>
                  <MenuItem value="storage">Storage</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>

              {/* Items List (for moving quotes) */}
              {formData.category === "moving" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Items to be Moved *"
                    value={formData.itemsList}
                    onChange={handleChange("itemsList")}
                    placeholder="Comma-separated list (e.g., Electronics (TV, computer), Furniture (sofas, tables, chairs), Gym equipment)"
                    helperText="List all items to be moved, separated by commas"
                    required
                    multiline
                    rows={3}
                  />
                </Grid>
              )}

              {/* Line Items Section */}
              {formData.items.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Line Items
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Add products from the catalog or enter custom items. Each
                    line item must have a description, unit price, and supplier
                    selected.
                  </Typography>
                </Grid>
              )}

              {formData.items.map((item, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={12}>
                    <Card
                      variant="outlined"
                      sx={{ p: 2, mb: 2, overflow: "visible" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          Line Item {index + 1}
                        </Typography>
                        <IconButton
                          onClick={() => removeLineItem(index)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <PriceCardLineItemEditor
                            lineItem={item}
                            onLineItemChange={replaceLineItem(index)}
                            onCreateProduct={(name) =>
                              openCreateProductModal(index, name)
                            }
                            onMissingTariff={onMissingTariff}
                            label="Tariff, Product or Custom Item"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Description *"
                            value={item.description}
                            onChange={handleLineItemChange(
                              index,
                              "description"
                            )}
                            required
                            placeholder="Item description"
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Quantity *"
                            type="number"
                            value={item.quantity || 1}
                            onChange={handleLineItemChange(index, "quantity")}
                            required
                            inputProps={{ min: 1, step: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Unit Price (R) *"
                            type="number"
                            value={item.unitPrice}
                            onChange={handleLineItemChange(index, "unitPrice")}
                            required
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <SupplierSelect
                            value={item.supplier || "bubakii"}
                            onChange={(supplier) => {
                              const newItems = [...formData.items];
                              newItems[index] = {
                                ...newItems[index],
                                supplier: supplier,
                              };
                              setFormData({
                                ...formData,
                                items: newItems,
                              });
                            }}
                            defaultSupplier={item.product?.defaultSupplier}
                            label="Supplier *"
                            required
                          />
                        </Grid>
                        {item.total > 0 && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Total"
                              value={formatCurrency(item.total || 0)}
                              disabled
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Card>
                  </Grid>
                </React.Fragment>
              ))}

              <Grid item xs={12}>
                <Button
                  startIcon={<Add />}
                  onClick={addLineItem}
                  variant="outlined"
                  size="small"
                >
                  Add Line Item
                </Button>
              </Grid>

              {/* SOP #1: Truck Size Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Truck Size
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Truck Size"
                  value={formData.truckSize}
                  onChange={handleChange("truckSize")}
                  helperText="Select the appropriate truck size for this move"
                >
                  <MenuItem value="">None selected</MenuItem>
                  <MenuItem value="1_ton">1 Ton</MenuItem>
                  <MenuItem value="2_ton">2 Ton</MenuItem>
                  <MenuItem value="4_ton">4 Ton</MenuItem>
                  <MenuItem value="8_ton">8 Ton</MenuItem>
                  <MenuItem value="1_ton_trailer">1 Ton with Trailer</MenuItem>
                  <MenuItem value="2_ton_trailer">2 Ton with Trailer</MenuItem>
                  <MenuItem value="4_ton_trailer">4 Ton with Trailer</MenuItem>
                  <MenuItem value="8_ton_trailer">8 Ton with Trailer</MenuItem>
                </TextField>
              </Grid>

              {(selectedLead || leadId) && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Service type"
                    value={formData.serviceType || ""}
                    onChange={handleChange("serviceType")}
                    helperText="Shared load vs Exclusive load — saved on the lead"
                  >
                    <MenuItem value="">Select service type</MenuItem>
                    <MenuItem value="shared_load">Shared load</MenuItem>
                    <MenuItem value="exclusive_load">Exclusive load</MenuItem>
                  </TextField>
                </Grid>
              )}

              {/* SOP #2: Load Type (required when leadId or selectedLead is present) */}
              {(selectedLead || leadId) && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Load Type *"
                    value={formData.loadType}
                    onChange={handleChange("loadType")}
                    required
                    error={error && !formData.loadType ? true : false}
                    helperText={
                      error && !formData.loadType
                        ? "Load type is required when creating quote from lead"
                        : "Select the load type for this quote (SOP #2)"
                    }
                  >
                    <MenuItem value="">Select load type</MenuItem>
                    <MenuItem value="small_load">Small Load</MenuItem>
                    <MenuItem value="medium_load">Medium Load</MenuItem>
                    <MenuItem value="large_load">Large Load</MenuItem>
                    <MenuItem value="full_load">Full Load</MenuItem>
                    <MenuItem value="share_load">Shared load</MenuItem>
                  </TextField>
                </Grid>
              )}

              {/* Note: Add-ons section removed - products should be added as line items */}

              {/* Quote Details */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Quote Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Estimated Cost"
                  type="number"
                  value={formData.estimatedCost}
                  onChange={handleChange("estimatedCost")}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Optional — enter manually or adopt from Quote Prep"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={formData.status}
                  onChange={handleChange("status")}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="negotiating">Negotiating</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange("notes")}
                  multiline
                  rows={4}
                  placeholder="Additional notes about this quote..."
                />
              </Grid>

              {/* Total Cost Display */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    bgcolor: "primary.50",
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="h6" color="primary">
                      Total Cost:
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(
                        resolveQuoteDisplayTotal(
                          calculateItemsTotal(formData.items),
                          formData.estimatedCost
                        )
                      )}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      gap: 2,
                      fontSize: "0.875rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Items Total:{" "}
                      <strong>
                        {formatCurrency(calculateItemsTotal(formData.items))}
                      </strong>
                    </Typography>
                    {formData.estimatedCost &&
                      parseFloat(formData.estimatedCost) > 0 &&
                      parseFloat(formData.estimatedCost) !==
                        calculateItemsTotal(formData.items) && (
                        <Typography variant="body2" color="text.secondary">
                          + Manual Override:{" "}
                          <strong>
                            {formatCurrency(parseFloat(formData.estimatedCost))}
                          </strong>
                        </Typography>
                      )}
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <EstimatePricingFab
                      leadId={
                        selectedLead?.id || selectedLead?._id || leadId || null
                      }
                      formSnapshot={{
                        from: formData.from,
                        to: formData.to,
                        serviceType: formData.serviceType,
                        itemsList: formData.itemsList,
                      }}
                    />
                  </Box>
                </Box>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/quotes")}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                    startIcon={<Refresh />}
                  >
                    Reset Form
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Save />
                    }
                    disabled={loading}
                  >
                    Create Quote
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
      <CreateProductModal
        open={createProductModal.open}
        onClose={closeCreateProductModal}
        title="Create Product from Line Item"
        initialValues={{
          name: createProductModal.name,
          description: createProductModal.name,
          unitPrice:
            createProductModal.index !== null
              ? Number(formData.items[createProductModal.index]?.unitPrice || 0)
              : 0,
          defaultSupplier:
            createProductModal.index !== null
              ? formData.items[createProductModal.index]?.supplier || "bubakii"
              : "bubakii",
          category: "services",
          active: true,
        }}
        onUseAsOneOff={() => {
          if (createProductModal.index === null) return;
          handleCustomItem(createProductModal.index)(createProductModal.name);
        }}
        onCreated={(product) => {
          if (createProductModal.index === null) return;
          handleProductSelect(createProductModal.index)(product);
        }}
      />
    </Box>
  );
};

QuoteForm.displayName = "QuoteForm";

export default QuoteForm;
